// CLIENT PORTAL RESTYLE — the client's light/dark choice.
//
// ⚠️⚠️ THEIR CHOICE CANNOT REACH US OR THEIR OTHER DEVICES. It lives in
// `localStorage` and nowhere else: no wire field, no portalUsers write, no
// cookie. There is nothing here worth a round trip, and storing a client's
// display preference on our side would be a record we would then have to keep
// correct.
//
// ⚠️ EVERY ACCESS IS WRAPPED. Some privacy modes THROW on `localStorage` rather
// than returning null, and an unhandled throw would white-screen the report for
// someone whose only mistake was browsing privately — the same trap
// `portalSignIn.ts` documents. The page must render correctly when the read
// comes back empty, which is what makes DARK the default rather than a stored
// value.

export type PortalReportMode = 'dark' | 'light';

/** ⚠️ DARK IS THE DEFAULT; the client toggles to light. */
export const DEFAULT_REPORT_MODE: PortalReportMode = 'dark';

export const REPORT_MODE_STORAGE_KEY = 'kyck-portal-report-mode';

/**
 * The attribute the token overrides hang off.
 *
 * ⚠️⚠️ IT GOES ON THE `.kyck-portal` ELEMENT, NEVER ON `:root` AND NEVER ON
 * `html`. Kyck internal must be visually untouched by this shipment, and a
 * global selector is the one mistake nothing else would catch — `noUnusedLocals`
 * is off and there is no eslint config.
 */
export const REPORT_MODE_ATTR = 'data-portal-mode';

/** Anything that is not a mode we recognise resolves to the default. */
export function parseReportMode(raw: string | null | undefined): PortalReportMode {
  return raw === 'light' || raw === 'dark' ? raw : DEFAULT_REPORT_MODE;
}

export function otherMode(mode: PortalReportMode): PortalReportMode {
  return mode === 'dark' ? 'light' : 'dark';
}

/** What the toggle's label says — it names the mode it will SWITCH TO. */
export function modeToggleLabel(mode: PortalReportMode): string {
  return mode === 'dark' ? 'Light mode' : 'Dark mode';
}

/**
 * A `Storage`-shaped thing, so the reader and writer below can be tested
 * against one that throws — which is the case that matters.
 */
export interface ModeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** ⚠️ Never throws. An unreadable store yields the default, which is correct. */
export function readReportMode(storage: ModeStorage | null | undefined): PortalReportMode {
  if (!storage) return DEFAULT_REPORT_MODE;
  try {
    return parseReportMode(storage.getItem(REPORT_MODE_STORAGE_KEY));
  } catch {
    return DEFAULT_REPORT_MODE;
  }
}

/**
 * ⚠️ Never throws, and reports NOTHING on failure. A client whose browser
 * refuses the write still gets a working toggle for this visit; telling them
 * their preference could not be saved would be noise about something they
 * cannot act on.
 */
export function writeReportMode(
  storage: ModeStorage | null | undefined,
  mode: PortalReportMode,
): void {
  if (!storage) return;
  try {
    storage.setItem(REPORT_MODE_STORAGE_KEY, mode);
  } catch {
    /* ignore — see the header */
  }
}
